import React, { useMemo, useState } from 'react';
import { Box, Alert, useTheme, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DeliveryStatus as DeliveryStatusEnum,
  MealOptionType,
  useGetDeliveryV2Query,
} from '@fdha/graphql-api-patient';
import {
  SectionType,
  DAYS_COVERED_I18N_KEY,
  buildDaysCoveredI18nParams,
  getDaysCoveredFallback,
} from '@fdha/common-utils';
import { Icon, Loader, Typography } from '@fdha/web-ui-library';
import { MenuOptionSelection as MealItem } from '@fdha/graphql-api-patient';
import { useMealDeliveryCounts } from '@fdha/common-hooks';

import { useReviewDeliveryData } from '../../hooks/useMealDeliveryContext';

import { getStyles } from './ReviewDelivery.styles';
import DishDetails from './DishDetails/DishDetails';

import {
  MealSection,
  DeliveryFooter,
  DeliveryEmptyState,
  DeliveryCancelMessage,
} from './index';

interface ReviewDeliveryProps {
  deliveryId: string;
  patientId?: string;
  foodProgramUserId: string;
  deliveryMenuId: string;
  status?: DeliveryStatusEnum | null;
  isCanceled?: boolean;
  allowSelfPreparedMeals?: boolean;
  onAddItems?: (itemType: SectionType) => void;
}

interface DishDetailsData {
  open: boolean;
  dishId: string;
  mealOptionType: MealOptionType;
  selectedVariantId?: string;
  selectedCount: number;
  requiredCount: number;
  menuOptionId: string;
  sectionName: SectionType;
}

const ReviewDelivery: React.FC<ReviewDeliveryProps> = ({
  deliveryId,
  foodProgramUserId,
  deliveryMenuId,
  status = DeliveryStatusEnum.Open,
  isCanceled = false,
  allowSelfPreparedMeals = false,
  onAddItems,
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const { t } = useTranslation('common');

  // Determine delivery state - following mobile's pattern
  const isOpen = status === DeliveryStatusEnum.Open;
  const isReadOnly = !isOpen || isCanceled;

  const { data: deliveryQueryData } = useGetDeliveryV2Query({
    variables: { deliveryId },
    skip: !deliveryId,
    fetchPolicy: 'cache-first',
    onError: (err) =>
      console.error('Failed to load delivery for daysCovered label', err),
  });
  const daysCoveredParams = useMemo(
    () =>
      buildDaysCoveredI18nParams(
        deliveryQueryData?.deliveryV2?.daysCovered ?? 0,
        deliveryQueryData?.deliveryV2?.weekdaysCovered ?? [],
        {
          isCanceled,
          formatWeekday: (day) =>
            t(`frequency.weekDays.upper.${day.toLowerCase()}`),
        }
      ),
    [
      deliveryQueryData?.deliveryV2?.daysCovered,
      deliveryQueryData?.deliveryV2?.weekdaysCovered,
      isCanceled,
      t,
    ]
  );
  const showDaysCovered = daysCoveredParams !== null;

  // Figma places the days-covered label inline with the "Open for changes
  // until …" status as a single subtitle row under the title (pipe-separated).
  // openChanges is already selected by GetDeliveryV2, so no query change.
  const openChangesDate = deliveryQueryData?.deliveryV2?.openChanges;
  const showOpenForChanges = isOpen && !isCanceled && !!openChangesDate;

  // Use unified delivery data from common hook (handles both open and closed)
  const deliveryData = useReviewDeliveryData();
  const { hasMultipleVariants } = deliveryData;
  const counts = useMealDeliveryCounts();

  // Determine loading state
  const loading = deliveryData.loading;

  // State for dish details dialog
  const [dishDetailsData, setDishDetailsData] = useState<DishDetailsData>({
    open: false,
    dishId: '',
    mealOptionType: MealOptionType.Dish,
    selectedCount: 0,
    requiredCount: 1,
    sectionName: 'entreeDishes',
    menuOptionId: '',
  });

  // Mobile-style handlers
  const hasSides = () => {
    if (!deliveryData.snacksAndSides) return false;

    return deliveryData.snacksAndSides.some(
      (item: any) =>
        item.name?.toLowerCase().includes('side') ||
        item.mealOptionType?.toString().toLowerCase().includes('side')
    );
  };

  const handleQuantityChange = (
    menuOptionId: string,
    quantity: number,
    mealkitVariantId?: string | null
  ) => {
    if (isOpen && deliveryData.updateSelection) {
      // For open deliveries, use shared state actions
      const isEntree = [
        ...(deliveryData.preparedMeals || []),
        ...(deliveryData.mealkits || []),
      ].some((item: any) => item.menuOptionId === menuOptionId);

      deliveryData.updateSelection({
        isEntree,
        menuOptionId,
        quantity,
        mealkitVariantId: mealkitVariantId || undefined,
      });
    }
    // For closed deliveries, quantity changes are not supported (read-only)
  };

  /**
   * Helper to identify whether the given item belongs to the entree section
   */
  const isEntreeItem = (menuOptionId: string): boolean => {
    if (!deliveryData) return false;

    const entreeMeals = [
      ...(deliveryData.preparedMeals || []),
      ...(deliveryData.mealkits || []),
    ];

    const isEntreeInOpenDelivery = entreeMeals.some(
      (meal: any) => meal.menuOptionId === menuOptionId
    );

    const isEntreeInClosedDelivery = isReadOnly
      ? Object.values(deliveryData.entreeSelections)
          .flat()
          .some((meal: any) => meal.menuOptionId === menuOptionId)
      : false;

    return isEntreeInOpenDelivery || isEntreeInClosedDelivery;
  };

  /**
   * Generates the DishDetailsData payload that will be consumed by the DishDetails modal.
   */
  const createDishDetailsData = (item: MealItem): DishDetailsData | null => {
    if (!deliveryData) return null;

    const isEntree = isEntreeItem(item.menuOptionId);
    const currentDisplay = isEntree
      ? deliveryData.entreeDisplay
      : deliveryData.snacksDisplay;

    const mealOptionType =
      item.mealOptionType === MealOptionType.MealKit
        ? MealOptionType.MealKit
        : MealOptionType.Dish;

    return {
      open: true,
      dishId: item.dishId,
      mealOptionType,
      selectedVariantId: item.mealkitVariantId || undefined,
      selectedCount: currentDisplay.selected,
      requiredCount: currentDisplay.total,
      menuOptionId: item.menuOptionId,
      sectionName: isEntree ? 'entreeDishes' : 'snacks',
    };
  };

  const handleItemClick = (item: MealItem) => {
    const detailsData = createDishDetailsData(item);
    if (detailsData) {
      setDishDetailsData(detailsData);
    }
  };

  const handleCloseDishDetails = () => {
    setDishDetailsData((prev) => ({ ...prev, open: false }));
  };

  // Mobile-style renderMenuSection function
  const renderMenuSection = () => {
    if (!deliveryData) return null;

    const hasSelections =
      deliveryData.entreeDisplay.selected > 0 ||
      deliveryData.snacksDisplay.selected > 0;

    // Show empty state for closed deliveries with no selections
    if (isReadOnly && !hasSelections) {
      return <DeliveryEmptyState isCanceled={isCanceled} />;
    }

    return (
      <>
        <MealSection
          title="Entree dishes"
          sectionType="entreeDishes"
          hasMultipleVariants={hasMultipleVariants}
          preparedItems={
            isReadOnly ? undefined : (deliveryData.preparedMeals as MealItem[])
          }
          mealKitItems={
            isReadOnly ? undefined : (deliveryData.mealkits as MealItem[])
          }
          items={
            isReadOnly
              ? (Object.values(
                  deliveryData.entreeSelections
                ).flat() as MealItem[])
              : undefined
          }
          selectedCount={deliveryData.entreeDisplay.selected}
          requiredCount={deliveryData.entreeDisplay.total}
          isComplete={true} // Always complete for read-only view
          isReadOnly={isReadOnly}
          allowSelfPreparedMeals={allowSelfPreparedMeals}
          onAddItems={onAddItems}
          onQuantityChange={handleQuantityChange}
          onItemClick={handleItemClick}
          showAddButton={!isReadOnly && !counts.isEntreesValid}
          addButtonText="ADD ENTREES"
          showMealTypeSeparation={isReadOnly}
        />

        {deliveryData.snacksDisplay.total > 0 && (
          <>
            <Divider sx={styles.sectionDivider} />
            <MealSection
              title="Snacks and sides"
              sectionType="snacks"
              hasMultipleVariants={hasMultipleVariants}
              items={
                isReadOnly
                  ? (Object.values(
                      deliveryData.snacksAndSidesSelections
                    ).flat() as MealItem[])
                  : (deliveryData.snacksAndSides as MealItem[])
              }
              selectedCount={deliveryData.snacksDisplay.selected}
              requiredCount={deliveryData.snacksDisplay.total}
              isComplete={true} // Always complete for read-only view
              isReadOnly={isReadOnly}
              allowSelfPreparedMeals={allowSelfPreparedMeals}
              onQuantityChange={handleQuantityChange}
              onAddItems={onAddItems}
              onItemClick={handleItemClick}
              showAddButton={!isReadOnly && !counts.isSnacksValid}
              addButtonText="ADD SNACKS/SIDES"
              showMealTypeSeparation={isReadOnly}
            />
          </>
        )}
      </>
    );
  };

  // Check if we should show empty state
  const hasSelections = deliveryData
    ? deliveryData.entreeDisplay.selected > 0 ||
      deliveryData.snacksDisplay.selected > 0
    : false;
  const showingEmptyState = isReadOnly && !hasSelections;

  if (loading) {
    return <Loader />;
  }

  // Check for actual errors from the delivery data
  const error = deliveryData.error;
  if (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error occurred';
    return (
      <Box sx={styles.errorContainer}>
        <Alert severity="error">
          <Typography
            i18nKey="meals:reviewDelivery.error.loadingMeals"
            i18nParams={{ error: errorMessage }}
          >
            Error loading meal options: {errorMessage}
          </Typography>
        </Alert>
      </Box>
    );
  }

  const selectedDishItem = {
    dishId: dishDetailsData.dishId,
    mealOptionType: dishDetailsData.mealOptionType,
    menuOptionId: dishDetailsData.menuOptionId,
    selectedVariantId: dishDetailsData.selectedVariantId,
    sectionName: dishDetailsData.sectionName,
  };

  return (
    <>
      {/* Cancel message - show when delivery is canceled */}
      {isCanceled && <DeliveryCancelMessage />}

      {(showOpenForChanges || (showDaysCovered && daysCoveredParams)) && (
        <Box sx={styles.daysCoveredRow}>
          {showOpenForChanges && (
            <Box sx={styles.daysCoveredItem}>
              <Icon
                name="unlock-outline"
                size="medium"
                fill={theme.palette.success.main}
                sx={styles.daysCoveredIcon}
              />
              <Typography
                variant="subtitle2"
                sx={styles.daysCoveredText}
                i18nKey="meals:card.status.open"
                i18nParams={{ date: openChangesDate }}
              >
                {`Open for changes until ${openChangesDate} at 9 AM ET`}
              </Typography>
            </Box>
          )}
          {showOpenForChanges && showDaysCovered && daysCoveredParams && (
            <Box component="span" sx={styles.daysCoveredSeparator}>
              |
            </Box>
          )}
          {showDaysCovered && daysCoveredParams && (
            <Box sx={styles.daysCoveredItem}>
              <Icon
                name="calendar-outline"
                size="medium"
                fill={theme.palette.warning.dark}
                sx={styles.daysCoveredIcon}
              />
              <Typography
                variant="subtitle2"
                sx={styles.daysCoveredText}
                i18nKey={DAYS_COVERED_I18N_KEY}
                i18nParams={daysCoveredParams}
              >
                {getDaysCoveredFallback(daysCoveredParams)}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {showingEmptyState ? (
        // Empty state without margin - let footer handle spacing
        renderMenuSection()
      ) : (
        // Regular content with white card background but no bottom margin
        <Box
          sx={{
            ...styles.mainContainer,
            marginBottom: 0, // Remove bottom margin since footer follows
          }}
        >
          {renderMenuSection()}
        </Box>
      )}

      {/* Footer with summary and actions - only show for open deliveries */}
      {isOpen && !isCanceled && (
        <Box
          sx={{
            ...styles.mainContainer,
            marginBottom: 0, // Remove bottom margin since this is the last element
            ...(showingEmptyState && {
              borderRadius: theme.spacing(0.625), // Add rounded corners when showing empty state
            }),
          }}
        >
          <DeliveryFooter
            entreesSelected={deliveryData.entreeDisplay.selected}
            entreesTotal={deliveryData.entreeDisplay.total}
            snacksSelected={deliveryData.snacksDisplay.selected}
            snacksTotal={deliveryData.snacksDisplay.total}
            hasSides={hasSides()}
            isValid={deliveryData.isValid}
            isDirty={deliveryData.isDirty}
            isSaving={deliveryData.isSaving}
            isReadOnly={isReadOnly}
            isCanceled={isCanceled}
            showingEmptyState={showingEmptyState}
            allowSelfPreparedMeals={allowSelfPreparedMeals}
            onSave={
              isOpen
                ? () =>
                    deliveryData.saveSelections({
                      foodProgramUserId,
                      deliveryMenuId,
                    })
                : () => {}
            }
          />
        </Box>
      )}

      {/* Dish Details Modal */}
      {dishDetailsData.open && (
        <DishDetails
          dishItem={selectedDishItem}
          selectedCount={dishDetailsData.selectedCount}
          requiredCount={dishDetailsData.requiredCount}
          onClose={handleCloseDishDetails}
          isPreviewMode={true}
          open={dishDetailsData.open}
        />
      )}
    </>
  );
};

export default ReviewDelivery;
